<template>
  <v-skeleton-loader
    :loading="showSkeletonLoader"
    :type="coronetSkeletonLoaderTypes.USERS_SETTINGS"
  >
    <table-wrapper class="mt-3" v-if="items.length > 0">
      <v-table class="white">
        <template #default>
          <thead>
            <tr>
              <th class="text-left caption">
                {{ $t("devicesSettings.malwareScan.table.os") }}
              </th>
              <th class="text-left caption">
                {{ $t("devicesSettings.malwareScan.table.action") }}
              </th>
              <th class="text-left caption">
                {{ $t("devicesSettings.malwareScan.table.labels") }}
              </th>
              <th class="text-left" :aria-label="$t('ariaLabels.actionsColumn')"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, i) in items"
              :key="item.id"
              :data-testid="`table-row-${i}`"
              class="malware-scan-row"
            >
              <td class="content-row">
                <div class="d-flex align-center">
                  <v-icon :icon="getOsIcon(item.osType)" :size="40" />
                  <span class="subtitle2 ml-3">
                    {{ $t(`general.osVersion.${camelCase(item.osType)}`) }}
                  </span>
                </div>
              </td>
              <td>
                <span class="body2"
                  >{{ $t("general.everyXDays", { n: item.daysCount }) }}&nbsp;</span
                >
                <span class="body2">{{ $t("general.at") }}&nbsp;</span>
                <span class="body2">{{
                  moment({
                    hour: item.preferredHour,
                    minutes: 0,
                  }).format("LT")
                }}</span>
              </td>
              <td>
                <coro-label
                  v-for="label in item.labels"
                  :key="label.name"
                  :background-color="label.color"
                  class="mr-2"
                >
                  {{ label.name }}
                </coro-label>
              </td>
              <td class="text-right" @click="$event.stopPropagation()">
                <v-menu bottom left>
                  <template v-slot:activator="{ props }">
                    <v-icon
                      icon="$dots"
                      v-bind="props"
                      :data-testid="`malware-scan-action-btn-${i}`"
                    />
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="(action, index) in actions"
                      :key="index"
                      :value="index"
                      :data-testid="`malware-scan-table-menu-item-${i}-${action.action}`"
                      @click="action.callback(item)"
                    >
                      <v-list-item-title>
                        {{ $t(`devicesSettings.malwareScan.table.actions.${action.action}`) }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </tbody>
        </template>
      </v-table>
    </table-wrapper>
    <div v-else class="d-flex align-center justify-center">
      <empty-state class="mt-12">
        <template #icon>
          <v-icon icon="$devices" :size="80" />
        </template>
        <template #description>
          {{ $t("devicesSettings.malwareScan.emptyState.description") }}
        </template>
        <template #subtitle>
          {{ $t("devicesSettings.malwareScan.emptyState.subtitle") }}
        </template>
        <template #button>
          <v-btn
            rounded
            color="primary"
            density="default"
            size="large"
            elevation="0"
            @click="addMalwareScan()"
          >
            {{ $t("devicesSettings.malwareScan.addBtn") }}
          </v-btn>
        </template>
      </empty-state>
    </div>
  </v-skeleton-loader>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { MalwareScanItem } from "@/_store/endpoint-security/malware-scan.module";
import { OsType } from "@/constants/devices";
import moment from "moment/moment";
import { coronetSkeletonLoaderTypes } from "@/constants/skeleton-loader";
import TableWrapper from "@/components/TableWrapper.vue";
import EmptyState from "@/components/EmptyState.vue";
import CoroLabel from "@/components/CoroLabel.vue";
import camelCase from "lodash/camelCase";
import cloneDeep from "lodash/cloneDeep";
import {
  defaultMalwareScanItem,
  MalwareScanActions,
} from "@/constants/endpoint-security-malware-scan";

export default defineComponent({
  components: {
    TableWrapper,
    EmptyState,
    CoroLabel,
  },
  props: {
    items: {
      type: Array as PropType<MalwareScanItem[]>,
      required: true,
    },
    showSkeletonLoader: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["on-action"],
  setup(_, { emit }) {
    const editItem = (item: MalwareScanItem) => {
      emit("on-action", {
        item,
        action: MalwareScanActions.EDIT,
      });
    };

    const deleteItem = (item: MalwareScanItem) => {
      emit("on-action", {
        item,
        action: MalwareScanActions.DELETE,
      });
    };

    const addMalwareScan = () => {
      emit("on-action", {
        item: cloneDeep(defaultMalwareScanItem),
        action: MalwareScanActions.CREATE,
      });
    };

    const actions = [
      {
        action: MalwareScanActions.EDIT,
        callback: editItem,
      },
      {
        action: MalwareScanActions.DELETE,
        callback: deleteItem,
      },
    ];

    const getOsIcon = (osType: string) => {
      switch (osType) {
        case OsType.WINDOWS:
          return "$windowsPc";
        case OsType.MAC_OS:
          return "$macPc";
        case OsType.LINUX:
          return "$linuxPc";
        default:
          return "$windowsPc";
      }
    };

    return {
      coronetSkeletonLoaderTypes,
      camelCase,
      moment,
      actions,
      addMalwareScan,
      getOsIcon,
    };
  },
});
</script>

<style scoped lang="scss">
.malware-scan-row {
  height: 72px;
}
</style>
